/**
 * Document Upload Service
 * Handles document uploads via gmid API and Firestore document listening
 * Upload and status mutations go through the API gateway; real-time listeners use Firestore SDK
 */

import { doc, onSnapshot } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { db } from './firebaseConfig';
import { getCurrentFirebaseToken } from '../utils/authUtils';
import {
  ProcessedDocumentMetadata,
  SUPPORTED_FILE_TYPES,
  UPLOAD_CONFIG,
} from '../types/documentUpload';

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

if (!BACKEND_BASE_URL) {
  throw new Error('REACT_APP_BACKEND_BASE_URL environment variable is required but not set');
}

const apiClient = axios.create({
  baseURL: BACKEND_BASE_URL,
  timeout: 600000, // 10 minutes — uploads can be large
});

/**
 * Generates a unique document ID
 */
export const generateDocumentId = (): string => {
  return uuidv4();
};

/**
 * Validates a single file
 */
export const validateFile = (file: File): { isValid: boolean; error?: string } => {
  // Check file size
  const maxSizeBytes = UPLOAD_CONFIG.MAX_FILE_SIZE_MB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      error: UPLOAD_CONFIG.VALIDATION_ERRORS.FILE_TOO_LARGE,
    };
  }

  // Check file type
  if (!Object.keys(SUPPORTED_FILE_TYPES).includes(file.type)) {
    return {
      isValid: false,
      error: UPLOAD_CONFIG.VALIDATION_ERRORS.UNSUPPORTED_TYPE,
    };
  }

  // Check if file is empty
  if (file.size === 0) {
    return {
      isValid: false,
      error: UPLOAD_CONFIG.VALIDATION_ERRORS.FILE_EMPTY,
    };
  }

  return { isValid: true };
};

/**
 * Validates multiple files
 * If more than MAX_FILES_PER_BATCH are selected, accepts first 10 and rejects the rest
 */
export const validateFiles = (
  files: FileList | File[]
): {
  valid: File[];
  invalid: { file: File; error: string }[];
} => {
  const fileArray = Array.from(files);
  const valid: File[] = [];
  const invalid: { file: File; error: string }[] = [];

  // Check total file count
  if (fileArray.length > UPLOAD_CONFIG.MAX_FILES_PER_BATCH) {
    // Accept first 10 files and validate them normally
    const acceptedFiles = fileArray.slice(0, UPLOAD_CONFIG.MAX_FILES_PER_BATCH);
    const rejectedFiles = fileArray.slice(UPLOAD_CONFIG.MAX_FILES_PER_BATCH);

    // Validate the first 10 files normally
    acceptedFiles.forEach(file => {
      const validation = validateFile(file);
      if (validation.isValid) {
        valid.push(file);
      } else {
        invalid.push({
          file,
          error: validation.error!,
        });
      }
    });

    // Mark remaining files as rejected with clear message
    rejectedFiles.forEach(file => {
      invalid.push({
        file,
        error: UPLOAD_CONFIG.VALIDATION_ERRORS.TOO_MANY_FILES,
      });
    });

    return { valid, invalid };
  }

  // Normal validation for 10 or fewer files
  fileArray.forEach(file => {
    const validation = validateFile(file);
    if (validation.isValid) {
      valid.push(file);
    } else {
      invalid.push({
        file,
        error: validation.error!,
      });
    }
  });

  return { valid, invalid };
};

/**
 * Uploads a document via the gmid API gateway
 * The server handles Firebase Storage upload and Firestore metadata creation
 */
export const uploadDocument = async (
  file: File,
  conversationId: string,
  messageId: string,
  documentId: string,
  onUploadProgress?: (progress: number) => void
): Promise<{ document_id: string; storage_path: string; status: string }> => {
  const token = await getCurrentFirebaseToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('conversation_id', conversationId);
  formData.append('message_id', messageId);
  formData.append('document_id', documentId);

  console.log(`[DocumentUpload] Starting upload via API:`, {
    documentId,
    fileName: file.name,
    mimeType: file.type,
    size: file.size,
  });

  const response = await apiClient.post<{
    document_id: string;
    storage_path: string;
    status: string;
  }>('/api/v1/documents', formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: event => {
      if (event.total && onUploadProgress) {
        onUploadProgress(Math.round((event.loaded * 100) / event.total));
      }
    },
  });

  console.log(`[DocumentUpload] Upload completed via API:`, {
    documentId: response.data.document_id,
    storagePath: response.data.storage_path,
    status: response.data.status,
  });

  return response.data;
};

/**
 * Marks documents as sent when message is submitted
 * Calls gmid API to update isMessageSent field
 */
export const markDocumentsAsSent = async (userId: string, documentIds: string[]): Promise<void> => {
  const token = await getCurrentFirebaseToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  try {
    console.log(`[DocumentUpload] Marking ${documentIds.length} documents as sent`);

    const updatePromises = documentIds.map(async documentId => {
      await apiClient.patch(
        `/api/v1/documents/${documentId}/sent`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log(`[DocumentUpload] Marked document as sent: ${documentId}`);
    });

    await Promise.all(updatePromises);
    console.log(`[DocumentUpload] All ${documentIds.length} documents marked as sent successfully`);
  } catch (error) {
    console.error('[DocumentUpload] Failed to mark documents as sent:', error);
    throw error;
  }
};

/**
 * Marks documents as deleted (logical delete) via gmid API
 * Used when:
 * - User removes attachment before sending
 * - User cancels upload
 * - Message with attachments is deleted
 * - Conversation with attachments is deleted
 */
export const markDocumentsAsDeleted = async (
  userId: string,
  documentIds: string[]
): Promise<void> => {
  // Handle empty array gracefully
  if (!documentIds || documentIds.length === 0) {
    console.log('[DocumentUpload] No documents to mark as deleted');
    return;
  }

  const token = await getCurrentFirebaseToken();
  if (!token) {
    console.warn('[DocumentUpload] Not authenticated, cannot mark documents as deleted');
    return;
  }

  console.log(`[DocumentUpload] Marking ${documentIds.length} documents as deleted`);

  // Track results for each document
  const updatePromises = documentIds.map(async documentId => {
    try {
      await apiClient.patch(
        `/api/v1/documents/${documentId}/deleted`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log(`[DocumentUpload] Marked document as deleted: ${documentId}`);
      return { success: true, documentId };
    } catch (error: any) {
      // Log but don't fail the entire batch if one document fails
      console.warn(`[DocumentUpload] Failed to mark document ${documentId} as deleted:`, {
        error: error.message,
        status: error.response?.status,
        documentId,
      });
      return { success: false, documentId, error: error.message };
    }
  });

  // Wait for all operations to complete (even those that failed)
  const results = await Promise.all(updatePromises);

  // Count successes and failures
  const successCount = results.filter(r => r.success).length;
  const failCount = results.filter(r => !r.success).length;

  // Log final results
  if (failCount === 0) {
    console.log(`[DocumentUpload] Successfully marked all ${successCount} documents as deleted`);
  } else {
    console.log(
      `[DocumentUpload] Marked ${successCount} documents as deleted successfully, ${failCount} failed`
    );
  }
};

/**
 * Sets up real-time listener for document processing updates
 * Returns unsubscribe function
 */
export const listenToDocumentMetadata = (
  userId: string,
  documentId: string,
  callback: (data: ProcessedDocumentMetadata | null) => void
): (() => void) => {
  const docRef = doc(db, `users/${userId}/documents/${documentId}`);

  console.log(`[DocumentUpload] Setting up listener for: ${documentId}`);

  const unsubscribe = onSnapshot(
    docRef,
    doc => {
      if (doc.exists()) {
        const data = doc.data() as ProcessedDocumentMetadata;
        console.log(`[DocumentUpload] Document updated:`, {
          documentId,
          status: data.status,
          hasExtractedText: !!data.extractedText,
        });
        callback(data);
      } else {
        console.warn(`[DocumentUpload] Document not found: ${documentId}`);
        callback(null);
      }
    },
    error => {
      console.error(`[DocumentUpload] Listener error for ${documentId}:`, error);
      callback(null);
    }
  );

  return unsubscribe;
};

/**
 * Gets error message for upload errors
 */
export const getUploadErrorMessage = (error: any): string => {
  if (error?.code === 'ERR_CANCELED' || error?.message === 'canceled') {
    return 'Upload cancelled';
  }
  if (error?.response?.status === 401 || error?.response?.status === 403) {
    return 'Unauthorized to upload files';
  }
  if (error?.response?.status === 413) {
    return 'File too large';
  }
  if (error?.response?.data?.detail) {
    return error.response.data.detail;
  }
  return error?.message || 'Upload failed';
};
